import { useForm } from "react-hook-form";
import ControlledInput from "Components/Input";
import { useTranslation } from "react-i18next";
import Button from "Components/Button";
import Space from "Components/Space";
import FlexRow from "Components/FlexRow";
import React, {useEffect, useMemo} from "react";
import {ShopifyOrderIntegrationDto} from "../../Apis/Models/ShopifyOrderIntegrationDto";
import Select from "../../Components/Select";
import ControlledSelect from "../../Components/Select/ControlledSelect";
import useFilteredList from "../../Hooks/UseFilteredList";
import {useAuthorizeLink, useMutateShopifyOrderIntegration} from "../../Apis/ShopifyOrderIntegrationApi";
import useQueryParams from "../../Hooks/UseQueryParams";
import useToasts from "../../Hooks/UseToasts";
import config from "../../Config";
import Label from "../../Components/Label";

interface IProps {
    entity?: ShopifyOrderIntegrationDto;
    onSubmit?: (data: ShopifyOrderIntegrationDto) => void;
}
const ShopifyOrderIntegrationForm = (props: IProps) => {
    
    const { t } = useTranslation();

    const [params] = useQueryParams();

    const { showSuccessToast } = useToasts();

    const { control, handleSubmit, reset, setValue } = useForm<ShopifyOrderIntegrationDto>();
    const { mutateAsync, isLoading } = useMutateShopifyOrderIntegration();
    const { mutateAsync: getAuthorizeLinkMutation } = useAuthorizeLink();

    const activeOptions = useMemo(() => [
        { value: false, label: t('inactive')},
        { value: true, label: t('active')},
    ], [t]);

    const [ filteredSettlement, setFilteredSettlement ] = useFilteredList(activeOptions, x => [x.label]);
    const [ filteredProduction, setFilteredProduction ] = useFilteredList(activeOptions, x => [x.label]);

    useEffect(() => {
        if (params['apiKey']) {
            setValue('apiKey', params['apiKey']?.toString());
        }
    }, [params, setValue])
    
    useEffect(() => {
        if (props.entity) {
            reset(props.entity);
        }
    }, [reset, props.entity])
    
    const onSubmit = async(data: ShopifyOrderIntegrationDto) => {
        await mutateAsync({ ...data });
        
        if (props.onSubmit) {
            props.onSubmit(data);
        }
        
        reset();
    }

    const getAuthorizeLink = async (data: ShopifyOrderIntegrationDto) => {
        await mutateAsync({ ...data });

        const link = await getAuthorizeLinkMutation({clientId: data.clientId, websiteUrl: data.websiteUrl});

        window.location.href = link;
    }

    const scopes = "read_assigned_fulfillment_orders,read_customers,read_fulfillments,read_inventory,read_locations,read_merchant_managed_fulfillment_orders,read_metaobject_definitions,write_metaobject_definitions,read_metaobjects,write_metaobjects,read_orders,read_product_feeds,read_products,write_products,read_returns"

    const copyScopes = () => {
        navigator.clipboard.writeText(scopes);

        showSuccessToast(t('scopesCopied'));
    }

    const copyRedirectUrl = () => {
        navigator.clipboard.writeText(config.shopifyRedirectUrl);

        showSuccessToast(t('redirectUrlCopied'));
    }

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Space direction={"vertical"} size={"large"}>

                    <div>1. Opret din app her: <a href={"https://dev.shopify.com/dashboard/130752486/apps"} target={"_blank"} rel={"noopener noreferrer"}>https://dev.shopify.com/dashboard/130752486/apps</a></div>
                    <div>
                        2. Udgiv en version med disse <span onClick={copyScopes} style={{textDecoration: 'underline'}}>scopes</span> og denne <span onClick={copyRedirectUrl} style={{textDecoration: 'underline'}}>Redirect Url</span>
                    </div>
                    <div>
                        3. Under "Home" Sæt distribution til Custom distribution og indtast url til shoppen
                    </div>
                    <div>
                        4. Kopier linket fra Distribution og Installer appen på shoppen
                    </div>
                    <div>
                        5. Udfyld oplysninger herunder. Du finder ClientId og ClientSecret på din app i Dev dashboard under Settings
                    </div>
                    <div>
                        6. Tryk på Hent Api nøgle i bunden
                    </div>

                    <ControlledInput name={"websiteUrl"} control={control} label={<Label placeholder={t('websiteUrl')} />} placeholder={"xxxx.myshopify.com"}  />
                    <ControlledInput name={"clientId"} control={control} placeholder={t('clientId')} />
                    <ControlledInput name={"clientSecret"} control={control} placeholder={t('clientSecret')} />

                    <ControlledInput name={"apiKey"} control={control} placeholder={t('apiKey')} />

                    <ControlledSelect control={control} placeholder={t('settlementActive')} name={"settlementActive"} onFilter={setFilteredSettlement}>
                        {filteredSettlement?.map(x => <Select.Option key={x.label} label={x.label} value={x.value} />)}
                    </ControlledSelect>

                    <ControlledSelect control={control} placeholder={t('productionImportActive')} name={"productionImportActive"} onFilter={setFilteredProduction}>
                        {filteredProduction?.map(x => <Select.Option key={x.label} label={x.label} value={x.value} />)}
                    </ControlledSelect>

                    <FlexRow justify={"space-between"}>
                        <Button text={t('fetchApiKey')} onClick={handleSubmit(getAuthorizeLink)} type={"tertiary"} />

                        <Button buttonType={"submit"} type={"primary"} text={t('save')} loading={isLoading} />
                    </FlexRow>
                </Space>
            </form>
        </>
    )
}

export default ShopifyOrderIntegrationForm
